import { useState } from 'react';
import { SectionHeader } from '@/components/ui/Primitives';
import { Reveal } from '@/components/ui/Reveal';
import { LevelLegend, TechRow } from '@/components/ui/TechRow';
import { cx } from '@/lib/format';
import type { SkillGroup, TechCategory } from '@/lib/types';
import { useI18n } from '@/store/preferences';

/**
 * Compact tabbed view for the landing page, where the stack is one section
 * among several and space is tight. The About page shows the same data
 * expanded — see `SkillsFull`.
 */
export function SkillsShowcase({ skills }: { skills: SkillGroup[] }) {
  const { t, locale } = useI18n();
  const groups = skills.filter((group) => group.items.length > 0);
  const [active, setActive] = useState<TechCategory | null>(null);

  if (groups.length === 0) return null;

  const current = active ?? groups[0].category;
  const items = groups.find((group) => group.category === current)?.items ?? [];
  const total = groups.reduce((sum, group) => sum + group.items.length, 0);

  return (
    <section className="section pt-0" id="stack">
      <div className="shell">
        <SectionHeader eyebrow={t.sections.skills} title={t.sections.skillsTitle} />

        <Reveal className="mb-8">
          <LevelLegend levelLegend={t.skills.levelLegend} countLegend={t.skills.countLegend} />
        </Reveal>

        <div className="grid gap-8 lg:grid-cols-[210px_minmax(0,1fr)] lg:gap-12">
          <Reveal>
            {/* Wraps on narrow screens instead of scrolling sideways: a
                horizontal scroller hides options off-screen with no hint that
                they exist. */}
            <ul className="flex flex-wrap gap-2 lg:flex-col lg:flex-nowrap lg:gap-1">
              {groups.map((group) => {
                const isActive = group.category === current;
                return (
                  <li key={group.category}>
                    <button
                      type="button"
                      onClick={() => setActive(group.category)}
                      aria-pressed={isActive}
                      className={cx(
                        'group/tab flex items-center gap-2 rounded-full border px-3.5 py-2',
                        'text-sm transition-all duration-300 ease-smooth',
                        'lg:w-full lg:justify-between lg:rounded-xl lg:border-transparent lg:px-3.5 lg:py-2.5',
                        isActive
                          ? 'border-accent/40 bg-accent/10 text-ink lg:border-transparent lg:bg-elevated'
                          : 'border-line text-muted hover:border-ink/25 hover:text-ink lg:hover:bg-elevated/50'
                      )}
                    >
                      <span className="flex items-center gap-2.5">
                        <span
                          className={cx(
                            'hidden h-4 w-px transition-all duration-300 lg:block',
                            isActive ? 'bg-accent' : 'bg-line group-hover/tab:bg-faint'
                          )}
                          aria-hidden
                        />
                        {t.category[group.category]}
                      </span>
                      <span className="font-mono text-2xs tabular text-faint">
                        {group.items.length}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            <p className="mt-5 hidden border-t border-line pt-4 font-mono text-2xs text-faint lg:block">
              {total} {locale === 'es' ? 'tecnologías en total' : 'technologies total'}
            </p>
          </Reveal>

          <div className="min-h-[16rem]">
            {/* Keyed on the category so the grid re-mounts and replays its
                entrance animation whenever the selection changes. */}
            <ul key={current} className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {[...items]
                .sort((a, b) => b.level - a.level || a.name.localeCompare(b.name))
                .map((tech, index) => (
                  <li
                    key={tech.id}
                    className="stagger-item stagger-in-self"
                    style={{ '--i': index } as React.CSSProperties}
                  >
                    <TechRow tech={tech} levelLabel={t.skills.level} usedIn={t.common.usedIn} />
                  </li>
                ))}
            </ul>

            <p className="mt-8 border-t border-line pt-4 text-xs leading-relaxed text-faint">
              {t.sections.skillsIntro}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
